import { SanityImageSource } from '@sanity/image-url/lib/types/types'
import Link from 'next/link'
import { MdChevronRight, MdDownload, MdLock } from 'react-icons/md'

import {
  BASE_CLASSES,
  VARIANT_CLASSES
} from 'src/components/atoms/ButtonRebrand/styles'
import {
  Heading4,
  ParagraphSmall
} from 'src/components/atoms/DesignSystem/Typography/Typography'
import { Image } from 'src/components/atoms/Image'
import { ExternalLinkComponent } from 'src/components/atoms/Link'
import { VideoDialog } from 'src/components/atoms/VideoPlayer/VideoDialog'

import { donatePath } from 'src/config/paths'
import { getSanityImageUrl } from 'src/utils/sanity'

export interface ResourceCardProps {
  image: SanityImageSource | string
  name: string
  description?: string
  resourceType: string
  resourceUrl: string
  disabled?: boolean
  gate?: number
}

export const ResourceCard = ({
  image,
  name,
  description,
  resourceType,
  resourceUrl,
  disabled = false,
  gate
}: ResourceCardProps) => {
  const isCMSImage = image && typeof image !== 'string'
  const imgSrc = isCMSImage ? getSanityImageUrl(image) : image
  const resource = disabled ? '/' : resourceUrl

  const ctaClassName = `${BASE_CLASSES} ${VARIANT_CLASSES.lime}`

  let cta: any
  if (resourceType === 'view') {
    cta = (
      <ExternalLinkComponent
        className={`${ctaClassName} space-x-1`}
        href={resource}
      >
        <span>View</span>
        <MdChevronRight />
      </ExternalLinkComponent>
    )
  }
  if (resourceType === 'download') {
    cta = (
      <ExternalLinkComponent
        className={`${ctaClassName} space-x-2`}
        href={resource}
      >
        <span>Download</span>
        <MdDownload />
      </ExternalLinkComponent>
    )
  }

  return (
    <div className='flex flex-col gap-3 relative lg:mr-5 justify-between h-full'>
      <div className='grow-0'>
        <div className='w-full relative mb-4 aspect-square'>
          <Image
            className={`w-full object-cover object-center${
              disabled ? ' opacity-60' : ''
            }`}
            alt={name}
            src={imgSrc}
            fill
          />
          {disabled && gate != null && (
            <div className='absolute inset-0 flex flex-col items-center justify-center gap-2'>
              <div className='bg-white rounded-full p-2 shadow-md'>
                <MdLock className='text-gray-500 text-2xl' />
              </div>
            </div>
          )}
          {!disabled && resourceType === 'video' && (
            <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
              <VideoDialog resource={resource} name={name} />
            </div>
          )}
        </div>
        {gate != null && (
          <div className='mb-2'>
            <span
              className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${
                disabled
                  ? 'bg-gray-50 border-gray-200'
                  : 'bg-green-50 text-green-700 border-green-200'
              }`}
            >
              {disabled ? (
                <>Fund {gate} trees to unlock</>
              ) : (
                <>&#10003; Available</>
              )}
            </span>
          </div>
        )}
        <Heading4 className='pb-3'>{name}</Heading4>
        <ParagraphSmall>{description}</ParagraphSmall>
      </div>
      <div className='flex flex-row items-center gap-3'>
        {!disabled && <div className='text-left'>{cta}</div>}
        {disabled && gate != null && (
          <Link
            href={donatePath()}
            className={`${BASE_CLASSES} ${VARIANT_CLASSES.forest}`}
          >
            Fund now
          </Link>
        )}
      </div>
    </div>
  )
}
